// src/lib/learn/topic-aliases.ts
//
// Resolve a /learn lesson's *casual* topic name to the official College
// Board taxonomy topic(s) the question bank actually files questions under
// (`Question.topic`).
//
// WHY THIS EXISTS
// ~half of the /learn lessons were authored under student-friendly names
// ("Quadratic Equations", "Punctuation", "Subject-Verb Agreement") — the
// phrasing students actually google, which is exactly what we want for SEO.
// But those names are NOT `Question.topic` values: the bank uses the official
// CB skill names ("Nonlinear Equations in 1 Variable and Systems of Equations
// in 2 Variables", "Boundaries", "Form, Structure, and Sense"). So a lesson's
// "Practice" button — which POSTs `{ topic: lesson.topic }` to
// /api/practice/start (an exact `where: { topic }` match) — found ZERO
// questions and died with "No questions found for this topic" on every
// casual-named lesson.
//
// This map is the single source of truth that lets the practice + hub-stats
// layers resolve a casual lesson topic to the real bank topic(s) so the drill
// pulls questions and recorded progress rolls up to the right skill.
//
// INVARIANTS (enforced by scripts/learn/validate-topic-aliases.ts + a unit test):
//   1. Every KEY is a real `Lesson.topic` that is NOT itself a `Question.topic`.
//   2. Every TARGET string is an exact, ACTIVE (`archived:false`) `Question.topic`.
//   3. Official-named topics are NOT keys here — `resolveBankTopics` returns
//      them unchanged, so all existing callers are unaffected (identity).

// Type-only import (erased at runtime) so this module stays free of the db
// singleton and is safe to import from client components.
import type { TopicDrillStats } from '@/lib/study-plan/learn-stats'
// The taxonomy is a pure data module (no imports), so this stays client-safe.
import { CANONICAL_TOPICS } from '@/lib/sat/taxonomy'

/**
 * Casual lesson topic  →  official bank topic(s).
 *
 * A casual topic maps to ≥1 official topic. Most map 1:1; a few span two
 * official skills (e.g. "Linear Equations" covers both the 1-variable and
 * 2-variable bank topics) and pull from both so the drill has a full pool.
 */
export const LESSON_TOPIC_ALIASES: Record<string, string[]> = {
  /* ----------------------------- Reading & Writing ----------------------------- */
  Punctuation: ['Boundaries'],
  'Subject-Verb Agreement': ['Form, Structure, and Sense'],
  'Verb Forms and Tenses': ['Form, Structure, and Sense'],
  'Pronoun Usage': ['Form, Structure, and Sense'],

  /* ---------------------------------- Math ------------------------------------- */
  'Quadratic Equations': [
    'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
  ],
  'Linear Equations': ['Linear Equations in 1 Variable', 'Linear Equations in 2 Variables'],
  'Systems of Equations': ['Systems of 2 Linear Equations in 2 Variables'],
  'Ratios and Proportions': ['Ratios, Rates, Proportional Relationships, and Units'],
  'Function Notation': ['Linear Functions', 'Nonlinear Functions'],
  'Exponents and Radicals': ['Equivalent Expressions'],
  'Exponential Functions': ['Nonlinear Functions'],
  Probability: ['Probability and Conditional Probability'],
  'Data Interpretation': ['Two-Variable Data: Models and Scatterplots'],
  Statistics: ['One-Variable Data: Distributions and Measures of Center and Spread'],
  'Right Triangles': ['Right Triangles and Trigonometry'],
  Polynomials: ['Nonlinear Functions', 'Equivalent Expressions'],
  'Unit Conversion': ['Ratios, Rates, Proportional Relationships, and Units'],
  'Linear Inequalities': ['Linear Inequalities in 1 or 2 Variables'],
  'Special Right Triangles': ['Right Triangles and Trigonometry'],
  'Similar Triangles': ['Lines, Angles, and Triangles'],
  Volume: ['Area and Volume'],
  'Arc Length': ['Circles'],
  'Angles and Lines': ['Lines, Angles, and Triangles'],
  Trigonometry: ['Right Triangles and Trigonometry'],
  'Area and Perimeter': ['Area and Volume'],

  /* ----------- Net-new long-tail SEO lessons (the phrasing students google,
                 each riding a real bank pool so its drill works day one) ------- */
  'Slope and Slope-Intercept Form': ['Linear Equations in 2 Variables', 'Linear Functions'],
  'Interpreting Linear Models': ['Linear Equations in 2 Variables', 'Linear Functions'],
  'Factoring Quadratics': [
    'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
    'Equivalent Expressions',
  ],
  'The Quadratic Formula': [
    'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
  ],
  'Completing the Square': [
    'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
  ],
  'Graphing Parabolas': ['Nonlinear Functions'],
  'Mean, Median, and Mode': ['One-Variable Data: Distributions and Measures of Center and Spread'],
  'Standard Deviation': ['One-Variable Data: Distributions and Measures of Center and Spread'],
  'Box Plots': ['One-Variable Data: Distributions and Measures of Center and Spread'],
  'Scatterplots and Line of Best Fit': ['Two-Variable Data: Models and Scatterplots'],
  'Two-Way Tables': ['Probability and Conditional Probability'],
  'Margin of Error': ['Inference from Sample Statistics and Margin of Error'],
  'Percent Change': ['Percentages'],
  'Word Problems in Algebra': ['Linear Equations in 1 Variable'],
  'The Pythagorean Theorem': ['Right Triangles and Trigonometry'],
  'Surface Area': ['Area and Volume'],
  'Distance and Midpoint Formulas': ['Coordinate Geometry'],
  'Parallel and Perpendicular Lines': ['Coordinate Geometry'],
  'Comma Rules': ['Boundaries'],
  'Semicolons and Colons': ['Boundaries'],
  'Apostrophes and Possessives': ['Boundaries'],
  'Misplaced and Dangling Modifiers': ['Form, Structure, and Sense'],
  'Parallel Structure': ['Form, Structure, and Sense'],
  'Transition Words': ['Transitions'],
}

/**
 * Resolve a lesson/practice topic to the bank topic(s) to query.
 * Returns the alias targets for a casual topic, or `[topic]` unchanged for an
 * official topic (or any topic not in the map). Always returns ≥1 entry.
 */
export function resolveBankTopics(topic: string): string[] {
  const aliased = LESSON_TOPIC_ALIASES[topic]
  return aliased && aliased.length > 0 ? aliased : [topic]
}

/** True when `topic` is a casual alias (i.e. it maps to a *different* bank topic). */
export function isAliasedTopic(topic: string): boolean {
  return Object.prototype.hasOwnProperty.call(LESSON_TOPIC_ALIASES, topic)
}

/** A topic name we know, and the bank topic(s) its questions are filed under. */
export type ResolvedTopic = { name: string; bankTopics: string[] }

const exactKey = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()
// Punctuation- and space-blind: "form structure and sense", "Two Variable Data
// Models and Scatterplots". Built from a closed list, and a test pins that no
// two names share a loose key, so this can't pick the wrong topic.
const looseKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

// Canonical topics first, so an official name always wins over an alias.
const KNOWN_TOPICS: readonly string[] = [...CANONICAL_TOPICS, ...Object.keys(LESSON_TOPIC_ALIASES)]

function indexBy(key: (s: string) => string): Map<string, string> {
  const m = new Map<string, string>()
  for (const name of KNOWN_TOPICS) if (!m.has(key(name))) m.set(key(name), name)
  return m
}
const BY_EXACT = indexBy(exactKey)
const BY_LOOSE = indexBy(looseKey)

/**
 * Resolve a topic name typed by a person or a model ("boundaries",
 * "Punctuation", "form, structure and sense") to a topic we know: a canonical
 * taxonomy topic, else a casual lesson alias. Case-insensitive, then
 * punctuation-blind. Null when it's neither — the caller says so rather than
 * running an exact-match query that finds nothing (Sage's drill tool used to,
 * and returned an empty drill as a success).
 *
 *   "boundaries"  → { name: 'Boundaries',  bankTopics: ['Boundaries'] }
 *   "punctuation" → { name: 'Punctuation', bankTopics: ['Boundaries'] }
 */
export function resolveTopicName(raw: string): ResolvedTopic | null {
  if (typeof raw !== 'string' || raw.trim().length === 0) return null
  const name = BY_EXACT.get(exactKey(raw)) ?? BY_LOOSE.get(looseKey(raw))
  if (!name) return null
  return { name, bankTopics: [...resolveBankTopics(name)] }
}

/**
 * Aggregate a user's per-topic drill stats for a /learn hub card, resolving a
 * casual lesson name to the official bank topic(s) progress is recorded under.
 * Without this, a casual-named lesson always reads "0 attempted" even after the
 * student drills it (the drill records under the official `Question.topic`).
 * Official topics resolve to themselves, so their cards are unchanged. Returns
 * `undefined` when the user has answered nothing across the resolved topics.
 */
export function drillStatsForTopic(
  byTopic: Map<string, TopicDrillStats>,
  topic: string,
): TopicDrillStats | undefined {
  let attempted = 0
  let weightedCorrect = 0
  for (const bankTopic of resolveBankTopics(topic)) {
    const s = byTopic.get(bankTopic)
    if (!s) continue
    attempted += s.attemptedCount
    weightedCorrect += (s.avgAccuracyPct / 100) * s.attemptedCount
  }
  if (attempted === 0) return undefined
  return {
    attemptedCount: attempted,
    avgAccuracyPct: Math.round((weightedCorrect / attempted) * 100),
  }
}
